import { useRef } from "react";

import { Navigate, Outlet, useLocation } from "react-router-dom";

import Routers from "../constants/routers";
import { useUser } from "../context/UserContext";

export const routePermissionMap = [
  // ===== Contracts =====
  {
    pattern: /^\/contracts(\/.*)?$/,
    code: "guarantee_contract"
  },
  {
    pattern: /^\/contract-renewals(\/.*)?$/,
    code: "guarantee_contract"
  },

  // ===== Application =====

  {
    pattern: /^\/application-intake$/,
    code: "application_entry"
  },

  {
    pattern: /^\/applications(\/.*)?$/,
    code: "application_mgmt"
  },

  // ===== Variable Cost =====
  {
    pattern: /^\/variable-cost-applications(\/.*)?$/,
    code: "variable_expense_request"
  },
  {
    pattern: /^\/variable-cost-registration$/,
    code: "variable_expense_entry"
  },

  // ===== Delinquents =====
  {
    pattern: /^\/delinquents(\/.*)?$/,
    code: "old_delinquent_mgmt"
  },
  {
    pattern: /^\/delinquents-poppo$/,
    code: "old_delinquent_mgmt"
  },

  // ===== Affiliates =====
  {
    pattern: /^\/affiliates(\/.*)?$/,
    code: "shop_mgmt"
  },
  {
    pattern: /^\/affiliate-inquiries(\/.*)?$/,
    code: "shop_mgmt"
  },
  {
    pattern: /^\/affiliate-applications(\/.*)?$/,
    code: "shop_mgmt"
  },
  {
    pattern: /^\/shop-registration-applies(\/.*)?$/,
    code: "shop_mgmt"
  },

  // ===== Items =====
  {
    pattern: /^\/items(\/.*)?$/,
    code: "product_mgmt"
  },
  {
    pattern: /^\/document-templates(\/.*)?$/,
    code: "product_mgmt"
  },

  // ===== Inclusive Contract =====
  // {
  //   pattern: /^\/inclusive-contract-application$/,
  //   code: "comprehensive_request_mgmt",
  // },
  {
    pattern: /^\/inclusive-contract-application-management(\/.*)?$/,
    code: "comprehensive_request_mgmt"
  },

  // ===== Subrogation =====
  {
    pattern: /^\/subrogation-payment(\/.*)?$/,
    code: "subrogation_request_mgmt"
  },
  // ===== Initial Billing (legacy) =====
  {
    pattern: /^\/initial-billing$/,
    code: "old_initial_claim_mgmt"
  },

  // ===== Finance (legacy cashflow) =====
  {
    pattern: /^\/finance$/,
    code: "old_cashflow_mgmt"
  },

  // ===== Owner Remittance =====
  {
    pattern: /^\/owner-remittance$/,
    code: "old_owner_transfer"
  },

  // ===== Settings =====
  {
    pattern: /^\/settings$/,
    code: "old_system_settings"
  },
  {
    pattern: /^\/system-settings(\/.*)?$/,
    code: "old_system_settings"
  },

  {
    pattern: /^\/staff$/,
    code: "old_staff"
  },

  // {
  //   pattern: /^\/subrogation-request-register(\/.*)?$/,
  //   code: "subrogation_request_entry",
  // },
  // ===== Equipment Warranty =====
  // {
  //   pattern: /^\/equipment-warranty(\/.*)?$/,
  //   code: "equipment_guarantee",
  // },

  // // ===== Customers =====
  // {
  //   pattern: /^\/customers$/,
  //   code: "customer_mgmt",
  // },

  // ===== Payment Management =====
  // {
  //   pattern: /^\/payment-management$/,
  //   code: "cashflow_mgmt",
  // },

  // ===== Buildings / Owners / Staff =====
  {
    pattern: /^\/buildings(\/.*)?$/i,
    code: "old_property_mgmt"
  },
  {
    pattern: /^\/owners$/,
    code: "old_landlord_mgmt"
  },

  // ===== Cancel Contract =====
  {
    pattern: /^\/cancel-contract(\/.*)?$/,
    code: "contract_cancel_list"
  },

  // ===== Notices =====
  {
    pattern: /^\/notices(\/.*)?$/,
    code: "notification_mgmt"
  },
  {
    pattern: /^\/notification-deliveries(\/.*)?$/,
    code: "notification_mgmt"
  }
];

function propertyRoutePath(pathname: string): string {
  // Match React Router's segment decoding while preserving encoded separators.
  // Other permission boundaries retain their existing pathname behavior.
  try {
    return pathname
      .split("/")
      .map((segment) => decodeURIComponent(segment).replace(/\//g, "%2F"))
      .join("/");
  } catch {
    return pathname;
  }
}

export function PermissionGuard() {
  const { userPermission } = useUser();
  const afterLoginRef = useRef(localStorage.getItem("AFTER_LOGIN") === "1");
  const location = useLocation();
  if (!userPermission) return null; // or <Spin />
  const matched = routePermissionMap.find((r) =>
    r.pattern.test(r.code === "old_property_mgmt" ? propertyRoutePath(location.pathname) : location.pathname)
  );

  // Route without permission
  if (!matched) {
    return <Outlet />;
  }

  const permission = userPermission.find((p: any) => p.code === matched.code);
  const firstAllowedRoute = getFirstAllowedRoute(userPermission, routePermissionMap);
  if (!permission || !permission.level || permission.level === 3) {
    if (afterLoginRef.current) {
      if (firstAllowedRoute) {
        localStorage.removeItem("AFTER_LOGIN");
        return <Navigate to={firstAllowedRoute} replace />;
      } else {
        localStorage.removeItem("AFTER_LOGIN");
        return <Navigate to={Routers.Forbidden} replace />;
      }
    } else {
      if (location?.state === "contract") {
        if (firstAllowedRoute) {
          return <Navigate to={firstAllowedRoute} replace />;
        }
        return <Navigate to={Routers.Forbidden} replace />;
      }
      return <Navigate to={Routers.Forbidden} replace />;
    }
  }
  localStorage.removeItem("AFTER_LOGIN");
  return <Outlet />;
}

export function getFirstAllowedRoute(
  userPermission: any[],
  routePermissionMap: { pattern: RegExp; code: string }[]
): string | null {
  for (const route of routePermissionMap) {
    const permission = userPermission.find((p) => p.code === route.code && p.level !== 3);

    if (permission) {
      // get path from regex
      const rawPath = route.pattern.source.replace("^\\/", "/").replace("(\\/.*)?$", "").replace("$", "");

      return rawPath;
    }
  }

  return null;
}
